<template>
  <div
    class="w-full h-full flex flex-col content-center bg-grey-10 text-black overflow-hidden lg:rounded-xl"
    :class="{ 'pt-12': isIos || isAndroid }"
  >
    <div class="h-[32px] flex flex-row justify-between items-center mx-[24px] mt-[11.5px] mb-5">
      <button @click="closeFilterModal">
        <img src="../../../public/assets/icons/close.svg" alt="" />
      </button>
      <h2 class="font-bold font-aktiv-grotesk text-[20px] leading-[25px]">
        {{ computedFilterHeader }}
      </h2>
      <p
        class="cursor-pointer font-aktiv-grotesk text-[14px] leading-[20px] font-semibold"
        :class="resetTextColor"
        @click="resetFilters"
      >
        {{ $t('copy.masterLayout.filterModal.resetFilters') }}
      </p>
    </div>

    <div class="accordion-wrapper h-full overflow-x-hidden overflow-y-scroll mx-[16px]">
      <div v-if="computedFilterItems.length === 0" class="p-4 text-center text-grey-60">
        {{ $t('copy.masterLayout.filterModal.noItems') }}
      </div>
      <div
        v-for="item in computedFilterItems"
        :key="getItemKey(item)"
        class="checkbox mb-[8px] rounded-lg"
        :class="getItemBackgroundClass(item)"
        @click="toggleSelectedItem(item)"
      >
        <label
          :for="getItemKey(item)"
          class="flex items-center h-full relative cursor-pointer pl-[16px] py-[8px] text-[16px] leading-[24px] lg:max-w-[35ch]"
          :style="{ width: 'calc(100% - 52px)' }"
        >
          <span
            >{{ getTitle(item) }}
            <small v-if="item.sessionId" class="text-xs italic whitespace-nowrap"
              >(Session {{ item.sessionId }})</small
            ></span
          >
          <input
            :id="getItemKey(item)"
            class="hidden absolute"
            type="checkbox"
            :name="getTitle(item)"
            :value="getItemValue(item)"
            :checked="hasChecked(item)"
            :data-event-tracking="
              'filter-' +
              getTitle(item)
                .toLowerCase()
                .replace(/[\s:]+/g, '-')
            "
            @click.stop
          />
          <div v-if="hasChecked(item)">
            <img
              class="absolute top-1/2 right-[-36px] w-[20px] h-[20px] rounded-sm transform -translate-y-1/2"
              :src="checkedIconSrc"
            />
          </div>
          <div v-else>
            <img
              class="absolute top-1/2 right-[-36px] w-[20px] h-[20px] rounded-sm transform -translate-y-1/2"
              :src="uncheckedIconSrc"
            />
          </div>
        </label>
      </div>
    </div>

    <div class="mx-[24px]">
      <BaseButton
        class="w-full h-[48px] justify-center items-center font-aktiv-grotesk leading-[24px] text-[18px] text-center py-[12px] text-white font-bold mt-[16px] mb-[100px] lg:mb-[16px]"
        @click="applyFilters"
        :solid-classes="buttonSolidClasses"
        :disabled="isFilterEmpty"
      >
        {{ $t('copy.masterLayout.filterModal.applyFilters') }}
      </BaseButton>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { isIosNativeOnly, isAndroidNativeOnly } from '@/utils/platform-check';
import { isGICPage } from '@/utils/conference-type';
import BaseButton from '../common/BaseButton.vue';

export default {
  components: {
    BaseButton,
  },
  props: {
    filterItems: {
      type: Array,
      default: () => [],
    },
    filterHeader: {
      type: String,
      default: '',
    },
    filterType: {
      type: String,
      default: '',
    },
    filterTypeId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      selectedItems: [],
    };
  },
  computed: {
    ...mapState('filter', [
      'filteredItems',
      'selectedFilter',
      'filteredSessions',
      'filteredTopics',
    ]),
    isIos() {
      return isIosNativeOnly();
    },
    isAndroid() {
      return isAndroidNativeOnly();
    },
    isFilterEmpty() {
      return this.selectedItems.length < 1;
    },
    // Always use props - simplified computed properties
    computedFilterItems() {
      return Array.isArray(this.filterItems) ? this.filterItems : [];
    },
    computedFilterHeader() {
      return this.filterHeader || '';
    },
    isGICPage() {
      return isGICPage();
    },
    checkedIconSrc() {
      if (this.isGICPage) {
        return require('../../../public/assets/icons/checked-icon-orange.png');
      }
      return require('../../../public/assets/icons/checked-icon.png');
    },
    uncheckedIconSrc() {
      if (this.isGICPage) {
        return require('../../../public/assets/icons/unchecked-icon-orange.png');
      }
      return require('../../../public/assets/icons/unchecked-icon.png');
    },
    buttonSolidClasses() {
      return this.isGICPage ? 'bg-[#FF6600] text-white' : 'bg-[#00A9E0] text-white';
    },
    resetTextColor() {
      return this.isGICPage ? 'text-[#FF6600]' : 'text-[#00A9E0]';
    },
  },
  watch: {
    // Watch for changes in props to update selectedItems
    filterItems: {
      handler() {
        this.initializeSelectedItems();
      },
      immediate: false,
    },
    filterTypeId: {
      handler() {
        this.initializeSelectedItems();
      },
      immediate: false,
    },
    // For GIC: use filterItems prop, for old system: use schedule
    displaySchedule() {
      if (this.filterItems) {
        return this.filterItems;
      }
      return this.schedule;
    },
  },
  mounted() {
    this.initializeSelectedItems();
  },
  methods: {
    async closeFilterModal() {
      const showFilterModal = false;
      // Maintain filterType when closing for GPC/GPCR
      if (this.filterTypeId === null && this.filterType) {
        await this.$store.dispatch('filter/updateShowFilterModal', {
          showFilterModal,
          filterType: this.filterType,
        });
      } else {
        await this.$store.dispatch('filter/updateShowFilterModal', {
          showFilterModal,
        });
      }
    },
    async setFilters() {
      // Use filterTypeId to detect GIC (has typeId) vs GPC/GPCR (typeId is null)
      if (this.filterTypeId !== null) {
        // GIC system
        await this.$store.dispatch('filter/setSelectedFilter', {
          type: this.filterType || 'Sessions',
          typeId: this.filterTypeId,
        });
        await this.$store.dispatch('filter/updateFilteredItems', {
          filteredItems: this.selectedItems,
        });
      } else {
        // GPC/GPCR system
        if (this.filterType === 'Topics') {
          await this.$store.dispatch('filter/updateFilteredValues', {
            filteredSessions: [],
            filteredTopics: this.selectedItems,
          });
        } else {
          await this.$store.dispatch('filter/updateFilteredValues', {
            filteredSessions: this.selectedItems,
            filteredTopics: [],
          });
        }
      }
    },
    needClearFilter() {
      // Only applies to GIC system (when filterTypeId is not null)
      if (
        this.filterTypeId === null ||
        !this.selectedFilter ||
        Object.keys(this.selectedFilter).length === 0
      ) {
        return false;
      }

      const clickedFilterKey = `${this.filterType}-${this.filterTypeId}`;
      const selectedFilterKey = `${this.selectedFilter.type}-${this.selectedFilter.typeId}`;
      return selectedFilterKey !== clickedFilterKey;
    },
    async applyFilters() {
      // Use filterTypeId to detect GIC vs GPC/GPCR
      if (this.filterTypeId !== null && this.needClearFilter()) {
        this.removePreviousFilterItems();
      }

      await this.setFilters(); // set checkbox items

      if (this.filterTypeId !== null) {
        // GIC system
        await this.$store.commit('filter/clearFilteredResources', {});
        await this.$store.dispatch('filter/updateFilteredResources');
        await this.$store.dispatch('filter/updateShowFilterResults', true);
      } else {
        // GPC/GPCR system
        await this.$store.dispatch('filter/updateFilteredMaterials');
        await this.$store.dispatch('filter/updateShowFilterResults', true);
      }

      this.closeFilterModal();
    },
    async resetFilters() {
      this.selectedItems = [];
      if (this.filterTypeId !== null) {
        // GIC system - keep selectedFilter for modal context, but clear filteredItems
        await this.$store.dispatch('filter/updateFilteredItems', {
          filteredItems: [],
        });
        await this.$store.commit('filter/clearFilteredResources', {});
        await this.$store.dispatch('filter/updateShowFilterResults', false);
      } else {
        // GPC/GPCR system
        await this.setFilters();
        await this.$store.dispatch('filter/updateShowFilterResults', false);
      }
    },
    removePreviousFilterItems() {
      const filterItemIds = this.computedFilterItems.map(item => this.getItemValue(item));
      this.selectedItems = this.selectedItems.filter(id => filterItemIds.includes(id));
    },
    getItemKey(item) {
      // Topics use 'topic' property, sessions use 'id'
      return item.id || item.topic || item.name;
    },
    getItemValue(item) {
      // Topics use 'topic' property, sessions use 'id'
      return item.id || item.topic || item.name;
    },
    getTitle(item) {
      // Topics use 'topic' property, sessions use 'title'
      return item.title || item.topic || item.name || '';
    },
    hasChecked(item) {
      const value = this.getItemValue(item);
      return this.selectedItems.includes(value);
    },
    getItemBackgroundClass(item) {
      const checked = this.hasChecked(item);
      if (checked) {
        return this.isGICPage ? 'bg-[rgba(255,102,0,0.1)]' : 'bg-[#e7f7ff]';
      }
      return 'bg-[#f8f9f9]';
    },
    toggleSelectedItem(item) {
      const value = this.getItemValue(item);
      if (this.selectedItems.includes(value)) {
        const index = this.selectedItems.indexOf(value);
        this.selectedItems.splice(index, 1);
      } else {
        this.selectedItems.push(value);
      }
    },
    initializeSelectedItems() {
      // Use filterTypeId to detect GIC (has typeId) vs GPC/GPCR (typeId is null)
      if (this.filterTypeId !== null) {
        // GIC system: use filteredItems from store
        this.selectedItems = Object.assign([], this.filteredItems || []);
      } else {
        // GPC/GPCR system: use filteredSessions or filteredTopics based on filterType prop
        if (this.filterType === 'Topics') {
          this.selectedItems = Object.assign([], this.filteredTopics || []);
        } else {
          this.selectedItems = Object.assign([], this.filteredSessions || []);
        }
      }
    },
  },
};
</script>

<style scoped>
.accordion-wrapper::-webkit-scrollbar {
  display: none;
}
</style>
